import { useState } from 'react';
import { Card, Button, Input, Badge } from '@atoms/index';

interface ReviewResponseModalProps {
  isOpen: boolean;
  reviewId: string;
  customerComment?: string;
  rating: number;
  onConfirm: (reviewId: string, response: string) => Promise<void>;
  onClose: () => void;
}

export function ReviewResponseModal({
  isOpen,
  reviewId,
  customerComment,
  rating,
  onConfirm,
  onClose,
}: ReviewResponseModalProps) {
  const [response, setResponse] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!response.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(reviewId, response);
      setResponse('');
      onClose();
    } catch (error) {
      console.error('Failed to add response:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center px-4">
      <button
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label="Modal kapat"
      />
      <Card className="relative z-[10000] w-full max-w-lg p-0 overflow-hidden">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Yorum Yanıtla</h3>

          <div className="space-y-4 mb-6">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span
                      key={star}
                      className={`text-lg ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <Badge variant="info">{rating}/5</Badge>
              </div>
              {customerComment && (
                <p className="text-sm text-gray-700 mt-2">{customerComment}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Yanıtınız *
              </label>
              <textarea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Müşteri yorumuna yanıt yazın..."
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                maxLength={500}
              />
              <p className="text-xs text-gray-500 mt-1">
                {response.length}/500 karakter
              </p>
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Vazgeç
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirm}
              disabled={!response.trim() || isSubmitting}
              isLoading={isSubmitting}
            >
              Yanıtla
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
